import {
  AnimatedContainer,
  AnimatedPage,
  AnimatedPageHeader
} from '@components/index'
import { Loader } from '@components/loader/Loader'
import { ApplicationForm } from '@features/applications/components/ApplicationForm'
import { Invitation } from '@features/applications/components/Invitation'
import { useCreateApplication } from '@services/applications/mutations/useCreateApplication'
import { usePatchApplication } from '@services/applications/mutations/usePatchApplication'
import { useApplication } from '@services/applications/queries/useApplication'
import dayjs from 'dayjs'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { ApplicationWrite } from 'types/api'

import type { ApartmentPrefill } from '@features/applications/utils/apartmentPrefill'

import { Switch } from '@/components/ui/switch'

export const defaultDateFormat = 'YYYY-MM-DD'

export function Application() {
  const { t } = useTranslation('application')
  const { state } = useLocation()
  const prefill = (state as { prefillFromApartment?: ApartmentPrefill } | null)
    ?.prefillFromApartment
  const { data, isLoading } = useApplication()
  const { mutateAsync: patchFromData, isPending: isPatchPending } =
    usePatchApplication()
  const { mutateAsync: createApplication, isPending: isCreatePending } =
    useCreateApplication()
  const isPending = isPatchPending || isCreatePending

  function submitForm(values: ApplicationWrite) {
    const updatedValues = {
      ...values,
      arrival_date: values.arrival_date
        ? dayjs(values.arrival_date).format(defaultDateFormat)
        : values.arrival_date
    }

    if (data?.id) {
      patchFromData({ id: data.id, body: updatedValues })
      return
    }

    createApplication(updatedValues)
  }

  function activateApplication(value: boolean) {
    if (!data?.id) return

    patchFromData({
      id: data.id,
      body: { status: value }
    })
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <AnimatedPage>
      <div className="mx-auto w-full max-w-[900px]">
        <AnimatedPageHeader
          title={t('form.labels.applications')}
          description={t('applicationDescription', {
            defaultValue:
              'Proces hľadania bývania začína podaním žiadosti, kde uvediete preferencie: lokalitu, veľkosť bytu, rozpočet a ďalšie požiadavky (napr. balkón, parkovanie). Systém vám potom bude posielať ponuky zodpovedajúce týmto kritériám priamo do vášho profilu.'
          })}
        />
        <Invitation />
        {data && (
          <AnimatedContainer staggerDelay={0.4}>
            <div className="flex items-center justify-end gap-2.5 rounded-2xl border border-ink/[0.06] bg-white p-4 shadow-[0_10px_30px_rgba(4,33,37,0.06)]">
              <span className="text-sm font-medium text-ink">
                {t('activateApplication')}
              </span>
              <Switch
                disabled={isPending}
                checked={!!data.status}
                onCheckedChange={activateApplication}
              />
            </div>
          </AnimatedContainer>
        )}
        <ApplicationForm
          data={data ?? undefined}
          prefill={prefill}
          handleSubmit={submitForm}
        />
      </div>
    </AnimatedPage>
  )
}
